import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { TrendingUp } from "lucide-react";
import { formatLeaderboardDuration } from "./helpers";

/**
 * Adjuster open-speed leaderboard. Shows email-domain bucket tiles + a
 * sortable per-recipient table. Hidden when no recipients exist.
 */
export function LeaderboardCard({ recipients }) {
  if (!recipients || !(recipients.recipients?.length > 0)) return null;
  return (
    <Card
      className="border-[#E5E7EB] mb-10"
      data-testid="adjuster-leaderboard-card"
    >
      <CardHeader>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <CardTitle className="font-['Outfit'] flex items-center gap-2">
            <TrendingUp className="w-4 h-4 text-[#C25934]" />
            Adjuster open-speed leaderboard
          </CardTitle>
          <p className="text-xs text-gray-500 max-w-md md:text-right">
            Time from emailed share-link → first open. Use this in
            partnership pitches: &ldquo;USAA opens packets in avg 14m.&rdquo;
          </p>
        </div>
      </CardHeader>
      <CardContent>
        {recipients.by_domain?.length > 0 && (
          <div
            className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6"
            data-testid="leaderboard-domain-tiles"
          >
            {recipients.by_domain.slice(0, 4).map((d) => (
              <div
                key={d.domain}
                className="rounded-md border border-[#E5E7EB] bg-white px-4 py-3"
                data-testid={`leaderboard-domain-${d.domain}`}
              >
                <div className="text-xs text-gray-500 truncate">
                  {d.domain}
                </div>
                <div className="text-xl font-['Outfit'] font-semibold text-[#1E3F33] mt-0.5">
                  {formatLeaderboardDuration(d.avg_open_seconds)}
                </div>
                <div className="text-[10px] text-gray-500 mt-0.5">
                  {d.opens} open{d.opens === 1 ? "" : "s"} ·{" "}
                  {d.unique_claims} claim{d.unique_claims === 1 ? "" : "s"}
                </div>
              </div>
            ))}
          </div>
        )}
        <div className="overflow-x-auto">
          <Table data-testid="leaderboard-table">
            <TableHeader>
              <TableRow>
                <TableHead>Recipient</TableHead>
                <TableHead className="text-right">Opens</TableHead>
                <TableHead className="text-right">Claims</TableHead>
                <TableHead className="text-right">Avg open</TableHead>
                <TableHead className="text-right">Median</TableHead>
                <TableHead className="text-right">Fastest</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {recipients.recipients.map((r) => (
                <TableRow
                  key={r.emailed_to}
                  data-testid={`leaderboard-row-${r.emailed_to}`}
                >
                  <TableCell className="font-medium text-[#1E3F33]">
                    {r.emailed_to}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {r.opens}
                  </TableCell>
                  <TableCell className="text-right font-mono">
                    {r.unique_claims}
                  </TableCell>
                  <TableCell className="text-right font-mono text-[#C25934] font-semibold">
                    {formatLeaderboardDuration(r.avg_open_seconds)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-gray-600">
                    {formatLeaderboardDuration(r.median_open_seconds)}
                  </TableCell>
                  <TableCell className="text-right font-mono text-emerald-700">
                    {formatLeaderboardDuration(r.fastest_seconds)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
}
